import * as PIXI from 'pixi.js';
import { Scene } from '../core/Scene';
import { LivesService } from '../payments/LivesService';

export class MenuScene extends Scene {
  private livesService = new LivesService();
  private livesText!: PIXI.Text;

  enter(): void {
    const bounds = this.game.getScreenBounds();
    const isTelegram = this.game.telegram.getIsTelegram();
    const user = this.game.telegram.getUser();
    const scale = Math.min(bounds.width, bounds.height) / 600;

    // Background
    const bg = new PIXI.Graphics();
    bg.rect(0, 0, bounds.width, bounds.height);
    bg.fill(0x1a1a2e);
    this.container.addChild(bg);

    // Title
    const title = new PIXI.Text({
      text: 'ХОМЯКИ ПРОТИВ СКАМА',
      style: {
        fill: 0xffd700,
        fontSize: Math.round(42 * scale),
        fontFamily: 'Arial',
        fontWeight: 'bold',
        align: 'center',
      },
    });
    title.anchor.set(0.5);
    title.x = bounds.width / 2;
    title.y = bounds.height * 0.15;
    this.container.addChild(title);

    // Subtitle
    const subtitle = new PIXI.Text({
      text: '«Хомяк, который не даст себя заскамить»',
      style: { fill: 0xaaaaaa, fontSize: Math.round(16 * scale), fontFamily: 'Arial', fontStyle: 'italic' },
    });
    subtitle.anchor.set(0.5);
    subtitle.x = bounds.width / 2;
    subtitle.y = bounds.height * 0.15 + Math.round(60 * scale);
    this.container.addChild(subtitle);

    // Lives display
    const lives = this.livesService.getTotal();
    const heartsText = '❤️'.repeat(Math.min(lives, 5)) + '🖤'.repeat(Math.max(0, 5 - lives));
    this.livesText = new PIXI.Text({
      text: `${heartsText} ${lives}/5`,
      style: { fill: 0xff4444, fontSize: Math.round(18 * scale), fontFamily: 'Arial' },
    });
    this.livesText.anchor.set(0.5);
    this.livesText.x = bounds.width / 2;
    this.livesText.y = bounds.height * 0.15 + Math.round(100 * scale);
    this.container.addChild(this.livesText);

    // User info
    if (user) {
      const userInfo = new PIXI.Text({
        text: `Привет, ${user.first_name}!`,
        style: { fill: 0x88ff88, fontSize: Math.round(18 * scale), fontFamily: 'Arial' },
      });
      userInfo.anchor.set(0.5);
      userInfo.x = bounds.width / 2;
      userInfo.y = bounds.height * 0.32;
      this.container.addChild(userInfo);
    }

    // Buttons layout
    const btnW = Math.round(220 * scale);
    const btnH = Math.round(55 * scale);
    const btnSpacing = Math.round(70 * scale);
    let currentY = bounds.height * 0.40;

    // Play button
    const playBtn = new PIXI.Container();
    const playBg = new PIXI.Graphics();
    playBg.roundRect(-btnW / 2, -btnH / 2, btnW, btnH, 14);
    playBg.fill(0x4CAF50);
    playBtn.addChild(playBg);
    const playText = new PIXI.Text({
      text: 'ИГРАТЬ',
      style: { fill: 0xffffff, fontSize: Math.round(28 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
    });
    playText.anchor.set(0.5);
    playBtn.addChild(playText);
    playBtn.x = bounds.width / 2;
    playBtn.y = currentY;
    playBtn.eventMode = 'static';
    playBtn.cursor = 'pointer';
    playBtn.on('pointerdown', () => {
      if (this.livesService.getTotal() > 0) {
        this.game.switchScene('game');
      } else {
        this.showNoLivesMessage(bounds, scale);
      }
    });
    this.container.addChild(playBtn);
    currentY += btnSpacing;

    // Level selection - two rows
    const levelsRow1 = [
      { id: 'level_1', label: '1' },
      { id: 'level_2', label: '2' },
      { id: 'level_3', label: '3' },
      { id: 'level_4', label: '4' },
      { id: 'level_5', label: '5' },
    ];
    const levelsRow2 = [
      { id: 'level_6', label: '6' },
      { id: 'level_7', label: '7' },
      { id: 'level_8', label: '8' },
      { id: 'level_9', label: '9' },
      { id: 'level_10', label: '10' },
    ];
    const lvlBtnSize = Math.round(40 * scale);
    const lvlSpacing = Math.round(48 * scale);

    // Row 1
    const lvlStartX1 = bounds.width / 2 - ((levelsRow1.length - 1) * lvlSpacing) / 2;
    for (let i = 0; i < levelsRow1.length; i++) {
      const lvl = levelsRow1[i]!;
      const lvlBtn = new PIXI.Container();
      const lvlBg = new PIXI.Graphics();
      lvlBg.roundRect(-lvlBtnSize / 2, -lvlBtnSize / 2, lvlBtnSize, lvlBtnSize, 8);
      lvlBg.fill(lvl.id === 'level_5' ? 0xff6600 : 0x444444);
      lvlBtn.addChild(lvlBg);
      const lvlText = new PIXI.Text({
        text: lvl.label,
        style: { fill: 0xffffff, fontSize: Math.round(14 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
      });
      lvlText.anchor.set(0.5);
      lvlBtn.addChild(lvlText);
      lvlBtn.x = lvlStartX1 + i * lvlSpacing;
      lvlBtn.y = currentY;
      lvlBtn.eventMode = 'static';
      lvlBtn.cursor = 'pointer';
      lvlBtn.on('pointerdown', () => {
        if (this.livesService.getTotal() > 0) {
          const scene = this.game.scenes.get('game');
          if (scene && 'setLevel' in scene) {
            (scene as { setLevel: (id: string) => void }).setLevel(lvl.id);
          }
          this.game.switchScene('game');
        } else {
          this.showNoLivesMessage(bounds, scale);
        }
      });
      this.container.addChild(lvlBtn);
    }
    currentY += btnSpacing * 0.7;

    // Row 2
    const lvlStartX2 = bounds.width / 2 - ((levelsRow2.length - 1) * lvlSpacing) / 2;
    for (let i = 0; i < levelsRow2.length; i++) {
      const lvl = levelsRow2[i]!;
      const lvlBtn = new PIXI.Container();
      const lvlBg = new PIXI.Graphics();
      lvlBg.roundRect(-lvlBtnSize / 2, -lvlBtnSize / 2, lvlBtnSize, lvlBtnSize, 8);
      lvlBg.fill(0x444444);
      lvlBtn.addChild(lvlBg);
      const lvlText = new PIXI.Text({
        text: lvl.label,
        style: { fill: 0xffffff, fontSize: Math.round(14 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
      });
      lvlText.anchor.set(0.5);
      lvlBtn.addChild(lvlText);
      lvlBtn.x = lvlStartX2 + i * lvlSpacing;
      lvlBtn.y = currentY;
      lvlBtn.eventMode = 'static';
      lvlBtn.cursor = 'pointer';
      lvlBtn.on('pointerdown', () => {
        if (this.livesService.getTotal() > 0) {
          const scene = this.game.scenes.get('game');
          if (scene && 'setLevel' in scene) {
            (scene as { setLevel: (id: string) => void }).setLevel(lvl.id);
          }
          this.game.switchScene('game');
        } else {
          this.showNoLivesMessage(bounds, scale);
        }
      });
      this.container.addChild(lvlBtn);
    }
    currentY += btnSpacing;

    // Shop button
    const shopBtn = new PIXI.Container();
    const shopBg = new PIXI.Graphics();
    shopBg.roundRect(-btnW / 2, -btnH / 2, btnW, btnH, 14);
    shopBg.fill(0x2196F3);
    shopBtn.addChild(shopBg);
    const shopText = new PIXI.Text({
      text: 'МАГАЗИН',
      style: { fill: 0xffffff, fontSize: Math.round(24 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
    });
    shopText.anchor.set(0.5);
    shopBtn.addChild(shopText);
    shopBtn.x = bounds.width / 2;
    shopBtn.y = currentY;
    shopBtn.eventMode = 'static';
    shopBtn.cursor = 'pointer';
    shopBtn.on('pointerdown', () => {
      this.game.switchScene('shop');
    });
    this.container.addChild(shopBtn);

    // Footer
    const footer = new PIXI.Text({
      text: isTelegram ? 'Telegram Mini App' : 'Mock Mode',
      style: { fill: 0x666666, fontSize: Math.round(12 * scale), fontFamily: 'Arial' },
    });
    footer.anchor.set(0.5);
    footer.x = bounds.width / 2;
    footer.y = bounds.height - 20;
    this.container.addChild(footer);
  }

  private showNoLivesMessage(bounds: { width: number; height: number }, scale: number): void {
    const msg = new PIXI.Text({
      text: 'Нет жизней! Купи в магазине',
      style: { fill: 0xff4444, fontSize: Math.round(18 * scale), fontFamily: 'Arial' },
    });
    msg.anchor.set(0.5);
    msg.x = bounds.width / 2;
    msg.y = bounds.height * 0.35;
    this.game.app.stage.addChild(msg);
    setTimeout(() => msg.destroy(), 2000);
  }

  exit(): void {
    this.container.removeChildren();
  }
}
